import { useState, useEffect } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, CalendarRange, BookOpen, Users2, ShieldAlert, Users, BarChart3,
  Award, FileText, Bell, Building2, ClipboardCheck, History, CreditCard, Settings,
  Menu, X, LogOut, ChevronDown, Mail, Send, UserPlus, HelpCircle,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useFiscalYear } from "@/context/FiscalYearContext";
import { api } from "@/lib/api";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";

const NAV = [
  { to: "/", label: "ホーム", icon: LayoutDashboard, roles: ["corp_admin", "office_admin", "staff"], end: true },
  { to: "/annual-plan", label: "年間計画", icon: CalendarRange, roles: ["corp_admin", "office_admin"] },
  { to: "/trainings", label: "研修", icon: BookOpen, roles: ["corp_admin", "office_admin", "staff"] },
  { to: "/onboarding", label: "入社研修", icon: UserPlus, roles: ["corp_admin", "office_admin", "staff"] },
  { to: "/committees", label: "委員会", icon: Users2, roles: ["corp_admin", "office_admin"] },
  { to: "/drills", label: "訓練", icon: ShieldAlert, roles: ["corp_admin", "office_admin"] },
  { to: "/staff", label: "職員", icon: Users, roles: ["corp_admin", "office_admin"] },
  { to: "/progress", label: "進捗管理", icon: BarChart3, roles: ["corp_admin", "office_admin"] },
  { to: "/certificates", label: "修了証", icon: Award, roles: ["corp_admin", "office_admin", "staff"] },
  { to: "/reports", label: "帳票", icon: FileText, roles: ["corp_admin", "office_admin"] },
  { to: "/notifications", label: "通知", icon: Bell, roles: ["corp_admin", "office_admin", "staff"] },
  { to: "/mail", label: "メール", icon: Mail, roles: ["corp_admin", "office_admin", "staff"] },
  { to: "/delivery-report", label: "配信レポート", icon: Send, roles: ["corp_admin", "office_admin"] },
  { to: "/offices", label: "事業所", icon: Building2, roles: ["corp_admin"] },
  { to: "/audit-mode", label: "実地指導", icon: ClipboardCheck, roles: ["corp_admin", "office_admin"] },
  { to: "/audit-logs", label: "操作履歴", icon: History, roles: ["corp_admin", "office_admin"] },
  { to: "/billing", label: "契約・請求", icon: CreditCard, roles: ["corp_admin"] },
  { to: "/settings", label: "設定", icon: Settings, roles: ["corp_admin"] },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const { years, fiscalYear, setFiscalYear } = useFiscalYear();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const items = NAV.filter((n) => n.roles.includes(user.role));
  const [unread, setUnread] = useState(0);

  useEffect(() => {
    const fetchUnread = () => api.get("/messages/unread-count").then(({ data }) => setUnread(data.count)).catch(() => {});
    fetchUnread();
    const t = setInterval(fetchUnread, 60000);
    window.addEventListener("wcc:unread", fetchUnread);
    return () => { clearInterval(t); window.removeEventListener("wcc:unread", fetchUnread); };
  }, []);

  useEffect(() => { setOpen(false); }, [navigate]);

  const Sidebar = (
    <aside className="flex flex-col h-full w-64 bg-white border-r border-slate-200">
      <div className="flex items-center gap-2.5 px-5 h-16 border-b border-slate-100">
        <img src="/wcc-icon.png" alt="WCC" className="w-9 h-9 rounded-lg object-contain" />
        <div className="leading-tight">
          <div className="font-bold text-slate-900">Welfy Care Campus</div>
          <div className="text-[10px] text-slate-400">障がい福祉 研修・法定管理</div>
        </div>
      </div>
      <nav className="flex-1 overflow-y-auto py-3 px-3 space-y-0.5">
        {items.map((n) => (
          <NavLink
            key={n.to}
            to={n.to}
            end={n.end}
            data-testid={`nav-${n.to === "/" ? "home" : n.to.slice(1)}`}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                isActive ? "bg-blue-50 text-blue-700" : "text-slate-600 hover:bg-slate-50"
              }`
            }
          >
            <n.icon className="w-[18px] h-[18px]" />
            <span className="flex-1">{n.label}</span>
            {n.to === "/mail" && unread > 0 && (
              <span data-testid="mail-unread-badge" className="text-[10px] bg-rose-500 text-white rounded-full px-1.5 py-0.5 leading-none">{unread}</span>
            )}
          </NavLink>
        ))}
      </nav>
    </aside>
  );

  return (
    <div className="flex h-screen overflow-hidden bg-slate-50">
      <div className="hidden lg:block">{Sidebar}</div>

      {/* mobile drawer */}
      {open && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <div className="absolute inset-0 bg-slate-900/40" onClick={() => setOpen(false)} />
          <div className="absolute left-0 top-0 h-full">{Sidebar}</div>
          <button className="absolute top-4 right-4 text-white" onClick={() => setOpen(false)}><X /></button>
        </div>
      )}

      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-16 bg-white border-b border-slate-200 flex items-center justify-between px-4 sm:px-6 gap-3 shrink-0">
          <div className="flex items-center gap-3">
            <button className="lg:hidden text-slate-600" data-testid="menu-toggle" onClick={() => setOpen(true)}><Menu /></button>
            <div className="hidden sm:block">
              <div className="text-sm font-semibold text-slate-800">{user.company?.name}</div>
              <div className="text-xs text-slate-400">{user.offices?.map((o) => o.name).join(" / ") || "全社"}</div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate("/guide")}
              data-testid="header-guide-btn"
              title="使い方ガイド"
              className="flex items-center gap-1.5 text-sm text-slate-600 hover:text-blue-700 rounded-lg px-2.5 py-1.5 hover:bg-slate-50 transition-colors"
            >
              <HelpCircle className="w-[18px] h-[18px]" />
              <span className="hidden sm:inline">使い方</span>
            </button>
            <div className="flex items-center gap-2" data-testid="fiscal-year-select">
              <span className="text-xs text-slate-400 hidden sm:inline">年度</span>
              <Select value={fiscalYear ? String(fiscalYear) : ""} onValueChange={setFiscalYear}>
                <SelectTrigger className="w-28 h-9"><SelectValue placeholder="年度" /></SelectTrigger>
                <SelectContent>
                  {years.map((y) => (
                    <SelectItem key={y.year} value={String(y.year)} data-testid={`fy-option-${y.year}`}>
                      {y.label}{y.is_current ? " (現)" : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="user-menu" className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-slate-50 transition-colors">
                  <div className="w-8 h-8 rounded-full bg-blue-600 text-white flex items-center justify-center text-sm font-medium">
                    {user.name?.[0]}
                  </div>
                  <div className="text-left hidden sm:block">
                    <div className="text-sm font-medium text-slate-800 leading-none">{user.name}</div>
                    <div className="text-[11px] text-slate-400 mt-0.5">{user.role_label}</div>
                  </div>
                  <ChevronDown className="w-4 h-4 text-slate-400" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-52">
                <DropdownMenuLabel>{user.name}（{user.role_label}）</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate("/settings")} data-testid="menu-settings">
                  <Settings className="w-4 h-4 mr-2" /> 設定
                </DropdownMenuItem>
                <DropdownMenuItem onClick={logout} data-testid="logout-btn" className="text-rose-600">
                  <LogOut className="w-4 h-4 mr-2" /> ログアウト
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        <main className="flex-1 overflow-y-auto p-4 sm:p-6 lg:p-8">
          <div className="max-w-7xl mx-auto wcc-fadein">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}
